import { useCallback } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import { format } from "date-fns";

import { useAppDispatch } from "app/hooks";
import {
  setContent,
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import { useDeleteStaffTaskCommentMutation } from "services/staff-tasks";
import type { TaskComment } from "services/types/staff-tasks.type";

type UserCommentInfoProps = {
  taskId: number;
  comment: TaskComment;
  isBusy?: boolean;
};

export default function UserCommentInfo({
  taskId,
  comment,
  isBusy
}: UserCommentInfoProps) {
  const dispatch = useAppDispatch();
  const [deleteComment, { isLoading }] = useDeleteStaffTaskCommentMutation();

  const createdAtDate = new Date(comment.created_at);
  const time = format(createdAtDate, "hh:mm aaa");

  const date = format(createdAtDate, "MMM dd, yyyy");

  const handleDelete = useCallback(() => {
    dispatch(setTitle("Are you sure you want to delete this comment?"));
    dispatch(
      setOnChange(() => {
        deleteComment({ taskId, commentId: comment.id });
      })
    );
    dispatch(setOpen(true));
    dispatch(setContent(`Delete "${comment.text}"?`));
  }, [dispatch, comment.text, comment.id, deleteComment, taskId]);

  return (
    <Stack direction="row" width="100%" columnGap={2} alignItems="center">
      <Stack flex={2.5} justifyContent="flex-end" width="100%" mt={2}>
        <Box
          id="test"
          display="flex"
          sx={{
            mr: 2,
            mb: 2,
            width: "1px",
            background: grey[400],
            height: "30px",
            alignSelf: "flex-end"
          }}
        ></Box>
        <Stack direction="row" width="100%" columnGap={1}>
          <Stack display="flex" flex={5} mt={-1.25}>
            <Typography
              variant="body1"
              sx={{ textAlign: "right", color: grey[700] }}
            >
              {time}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: "right", fontSize: "12px", color: grey[800] }}
            >
              {date}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 0.5,
                textAlign: "right",
                color: grey[700],
                fontSize: "13px",
                lineHeight: 1.2
              }}
            >
              {comment.created_by.full_name}
            </Typography>
          </Stack>
          <Box display="flex">
            <Avatar
              alt="avatar"
              src={comment.created_by.photo ?? ""}
              sx={{
                border: `solid 1px ${grey[400]}`,
                width: "32px",
                height: "32px"
              }}
              slotProps={{ img: { loading: "lazy" } }}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack flex={5} alignSelf="flex-end" mt={2}>
        <Stack direction="row">
          <Typography
            p={2}
            width="100%"
            variant="body2"
            sx={{
              boxSizing: "border-box",
              borderRadius: "6px",
              background: grey[100]
            }}
          >
            {comment.text}
          </Typography>
          <Box>
            <IconButton
              sx={{ transform: "scale(0.95)", mb: -1 }}
              disabled={isLoading || isBusy}
              onClick={handleDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}
